(() => {
  if (customElements.get('pb-live-product-slider')) return;

  const labels = JSON.parse(document.querySelector('[data-pb-live-slider-labels]').textContent);
  let sequence = 0;

  class PBProductSlider extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.firstElementChild;
      if (!this.track?.children.length) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.items = Array.from(this.track.children);
      this.track.classList.add('pb-live-product-track');
      this.track.id ||= `pb-live-product-track-${++sequence}`;
      this.track.tabIndex = 0;
      const heading = this.closest('.custom-complementary, .product-recommendations--container')?.querySelector('h2');
      if (heading) this.track.setAttribute('aria-label', heading.textContent.trim());

      this.controls = document.createElement('div');
      this.controls.className = 'pb-live-slider-controls';
      this.previous = this.createButton(labels.previous, 'M13 4l-6 6 6 6');
      this.next = this.createButton(labels.next, 'M7 4l6 6-6 6');
      this.status = document.createElement('span');
      this.status.className = 'pb-live-slider-status';
      this.status.setAttribute('role', 'status');
      this.status.setAttribute('aria-live', 'polite');
      this.controls.append(this.previous, this.status, this.next);
      this.append(this.controls);

      this.previous.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.track.addEventListener('scroll', () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
      }, { ...options, passive: true });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.move(event.key === 'ArrowRight' ? 1 : -1);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.scrollTo(event.key === 'Home' ? 0 : this.track.scrollWidth);
        }
      }, options);
      this.track.addEventListener('dragstart', event => event.preventDefault(), options);
      this.track.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button, input, select, textarea')) return;
        this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, scroll: this.track.scrollLeft, dragging: false };
      }, options);
      this.track.addEventListener('pointermove', event => {
        const pointer = this.pointer;
        if (!pointer || pointer.id !== event.pointerId) return;
        const x = event.clientX - pointer.x;
        const y = event.clientY - pointer.y;
        if (!pointer.dragging && Math.abs(x) > 8 && Math.abs(x) > Math.abs(y)) {
          pointer.dragging = true;
          this.track.setPointerCapture(event.pointerId);
          this.track.classList.add('is-dragging');
          this.track.style.scrollSnapType = 'none';
        }
        if (!pointer.dragging) return;
        event.preventDefault();
        this.track.scrollLeft = pointer.scroll - x;
      }, options);
      const finish = event => {
        const pointer = this.pointer;
        if (!pointer || pointer.id !== event.pointerId) return;
        if (pointer.dragging) {
          this.suppressClick = true;
          clearTimeout(this.clickTimer);
          this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 0);
        }
        this.pointer = null;
        this.track.classList.remove('is-dragging');
        this.track.style.removeProperty('scroll-snap-type');
      };
      this.track.addEventListener('pointerup', finish, options);
      this.track.addEventListener('pointercancel', finish, options);
      this.track.addEventListener('lostpointercapture', finish, options);
      this.track.addEventListener('pointerleave', event => {
        if (!this.pointer?.dragging) finish(event);
      }, options);
      this.track.addEventListener('click', event => {
        if (!this.suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
      }, { ...options, capture: true });
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);
      this.update();
    }

    createButton(label, path) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'pb-live-slider-button';
      button.setAttribute('aria-label', label);
      button.setAttribute('aria-controls', this.track.id);
      button.innerHTML = `<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="${path}"/></svg>`;
      return button;
    }

    move(direction) {
      const width = this.items[0].getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = width + gap;
      const index = Math.round(this.track.scrollLeft / step);
      this.scrollTo((index + direction) * step);
    }

    scrollTo(left) {
      const maximum = this.track.scrollWidth - this.track.clientWidth;
      this.track.scrollTo({ left: Math.max(0, Math.min(left, maximum)), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }

    update() {
      if (!this.track.clientWidth) return;
      const maximum = this.track.scrollWidth - this.track.clientWidth;
      this.controls.hidden = maximum <= 2;
      this.previous.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = this.track.scrollLeft >= maximum - 2;
      const track = this.track.getBoundingClientRect();
      const visible = this.items.map((item, index) => ({ index, box: item.getBoundingClientRect() })).filter(({ box }) => box.right > track.left + 2 && box.left < track.right - 2);
      if (visible.length) {
        const text = `${visible[0].index + 1}–${visible[visible.length - 1].index + 1} / ${this.items.length}`;
        if (this.status.textContent !== text) this.status.textContent = text;
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.clickTimer);
      this.controls?.remove();
      this.track?.classList.remove('is-dragging');
      this.track?.style.removeProperty('scroll-snap-type');
      this.pointer = null;
    }
  }

  customElements.define('pb-live-product-slider', PBProductSlider);

  const observers = new Map();
  const upgrade = root => {
    root.querySelectorAll('.pb-live-product-card--hover').forEach(card => {
      if (card.dataset.pbHoverPrepared) return;
      card.dataset.pbHoverPrepared = 'true';
      const image = card.querySelector('.pb-live-card-image--second');
      if (!image) return;
      const ready = () => {
        if (image.naturalWidth > 0) card.classList.add('pb-live-product-card--hover-ready');
      };
      if (image.complete) ready();
      else image.addEventListener('load', ready, { once: true });
    });
    root.querySelectorAll('.custom-complementary-grid, [data-product-recommendations] [data-product-row]').forEach(track => {
      if (!track.children.length || track.parentElement.localName === 'pb-live-product-slider') return;
      const slider = document.createElement('pb-live-product-slider');
      track.before(slider);
      slider.append(track);
      slider.connectedCallback();
    });
  };
  const initialize = root => {
    upgrade(root);
    root.querySelectorAll('[data-product-recommendations]').forEach(container => {
      if (observers.has(container)) return;
      const observer = new MutationObserver(() => {
        if (container.isConnected) upgrade(container.parentElement);
      });
      observer.observe(container, { childList: true, subtree: true });
      observers.set(container, observer);
    });
  };
  initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    observers.forEach((observer, container) => {
      if (!container.isConnected || event.target.contains(container)) {
        observer.disconnect();
        observers.delete(container);
      }
    });
  });
})();
